import ContentHeader from "../components/common/content/content-header";
import { StyledContainer } from "../components/common/content/styles";
import { Skeleton, Typography } from "@mui/material";
import ContentDetailPageHeader from "../components/common/content/content-detail-page-header";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import { portableTextComponents } from "../sanity/portableText";
import { ContentCategory, ContentItem } from "../sanity/types";
import { getContentItemBySlug } from "../sanity/queries";
import { fallbackLatestNews, fallbackMinistries } from "../sanity/fallbacks";
import { urlFor } from "../sanity/image";
import DOMPurify from "dompurify";
import {Seo, siteUrl} from "../seo/Seo";
import {AlertToast} from "../components/common/alert-toast";

interface Props {
  contentCategory: ContentCategory;
}

export default function ContentDetail({ contentCategory }: Props) {
  const { slug } = useParams();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!slug) {
      setContent(null);
      setLoading(false);
      return;
    }
    const fallbackList =
      contentCategory === "ministry" ? fallbackMinistries : fallbackLatestNews;
    const fallbackContent =
      fallbackList.find((item) => item.slug === slug) || null;
    let isMounted = true;
    getContentItemBySlug(contentCategory, slug)
      .then((data: ContentItem | null) => {
        if (!isMounted) {
          return;
        }
        if (!data) {
          setContent(fallbackContent);
          return;
        }
        setContent({
          ...(fallbackContent || {}),
          ...data,
        });
      })
      .catch(() => {
        if (isMounted) {
          setContent(fallbackContent);
          setErrorMessage("Unable to load content. Showing fallback content.");
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [contentCategory, slug]);

  const imageUrl = content?.image
    ? urlFor(content.image).width(1600).url()
    : content?.imageUrl
    ? process.env.PUBLIC_URL + content.imageUrl
    : "";
  const sanitizedHtml = content?.bodyHtml
    ? DOMPurify.sanitize(content.bodyHtml)
    : "";
  const seoDescription =
    content?.metaDescription ||
    content?.excerpt ||
    content?.bodyText ||
    "";
  const detailPath =
    content && slug
      ? `/${content.category === "ministry" ? "ministries" : "latest-news"}/${slug}`
      : "/";
  const canonicalUrl = `${siteUrl}${detailPath}`;
  const articleJsonLd =
    contentCategory === "latestNews" && content
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: content.title,
          url: canonicalUrl,
          datePublished: content.publishedAt,
          image: imageUrl || undefined,
        }
      : undefined;

  return (
    <>
      <Seo
        title={content?.metaTitle || content?.title}
        description={seoDescription}
        path={detailPath}
        type="article"
        image={imageUrl}
        jsonLd={articleJsonLd}
      />
      <StyledContainer maxWidth={false}>
        <ContentDetailPageHeader/>
        <AlertToast
          open={!!errorMessage}
          message={errorMessage}
          onClose={() => setErrorMessage("")}
          severity="warning"
        />
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={40} sx={{mx: "auto", my: 2}} />
            <Skeleton variant="rectangular" width="100%" height={300} />
          </>
        ) : (
          content && (
            <>
              <ContentHeader title={content.title} />
              {content.body ? (
                <PortableText
                  value={content.body}
                  components={portableTextComponents}
                />
              ) : sanitizedHtml ? (
                <Typography
                  color="text.primary"
                  variant="body1"
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              ) : content.bodyText || content.excerpt ? (
                <Typography
                  color="text.primary"
                  variant="body1"
                  fontWeight="bold"
                  textAlign={"center"}
                >
                  {content.bodyText || content.excerpt}
                </Typography>
              ) : null}
              <br />
              {imageUrl && (
                <img
                  width="100%"
                  src={imageUrl}
                  loading="lazy"
                  alt={content.image?.alt || content.title}
                />
              )}
            </>
          )
        )}
      </StyledContainer>
    </>
  );
}
